/**
 * Profile Completion Helpers
 * Calculate and manage user profile completion status
 */

/**
 * Calculate profile completion percentage based on required fields
 * @param {Object} userData - User data from Firestore
 * @returns {Object} - { percentage, missingFields, isComplete }
 */
export const calculateProfileCompletion = (userData) => {
  if (!userData) {
    return { percentage: 0, missingFields: [], isComplete: false };
  }

  // Define required fields for academic details
  const requiredFields = [
    { key: 'mobile', label: 'Mobile Number' },
    { key: 'class', label: 'Class' },
    { key: 'preparingFor', label: 'Preparing For' },
    { key: 'address', label: 'Address' }
  ];

  const filledFields = [];
  const missingFields = [];

  requiredFields.forEach(field => {
    const value = userData[field.key];
    if (value && value.trim() !== '' && value !== 'Not set') {
      filledFields.push(field);
    } else {
      missingFields.push(field);
    }
  });

  const percentage = Math.round((filledFields.length / requiredFields.length) * 100);
  const isComplete = percentage === 100;

  return {
    percentage,
    missingFields,
    isComplete,
    totalFields: requiredFields.length,
    filledCount: filledFields.length
  };
};

/**
 * Get profile completion message based on percentage
 * @param {number} percentage - Completion percentage
 * @returns {string} - Motivational message
 */
export const getCompletionMessage = (percentage) => {
  if (percentage === 100) return "Your profile is complete! 🎉";
  if (percentage >= 75) return "Almost there! Complete your profile to unlock all features.";
  if (percentage >= 50) return "You're halfway there! Keep going.";
  if (percentage >= 25) return "Great start! Add more details to your profile.";
  return "Let's get started! Complete your profile to personalize your experience.";
};

/**
 * Get completion status color
 * @param {number} percentage - Completion percentage
 * @returns {string} - Color code
 */
export const getCompletionColor = (percentage) => {
  if (percentage === 100) return '#10b981'; // Green
  if (percentage >= 75) return '#f59e0b'; // Orange
  if (percentage >= 50) return '#ef4444'; // Red
  return '#6b7280'; // Gray
};
